import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import {
  AdvancedFiltersState,
  AdvancedFilterOptions,
  defaultAdvancedFiltersState,
} from "../../models/advancedFilters";
import { FilterConfig } from "../../models/advancedFilterConfig";
import ToolsFilterOption from "./AdvancedFiltersOptions/ToolsFilterOption";
import PackagesFilterOption from "./AdvancedFiltersOptions/PackagesFilterOption";
import RolesFilterOption from "./AdvancedFiltersOptions/RolesFilterOption"; 

interface AdvancedFiltersDialogProps {
  open: boolean;
  filters: AdvancedFiltersState;
  filterOptions: AdvancedFilterOptions;
  filterConfig: FilterConfig;
  isLoading: boolean;
  onApply: (filters: AdvancedFiltersState) => void;
  onClose: () => void;
}

const AdvancedFiltersDialog = ({
  open,
  filters,
  filterOptions,
  filterConfig,
  isLoading,
  onApply,
  onClose,
}: AdvancedFiltersDialogProps): JSX.Element => {
  const [localFilters, setLocalFilters] =
    useState<AdvancedFiltersState>(filters);

  const showPackages = filterConfig.enabledFilters.includes("packages");
  const showTools = filterConfig.enabledFilters.includes("tools");
  const showRoles = filterConfig.enabledFilters.includes("roles");

  useEffect(() => {
    if (open) setLocalFilters(filters);
  }, [open, filters]);

  useEffect(() => {
    if (localFilters.selectedTools.length > 0 && !localFilters.selectedMode && filterOptions.modes.length > 0) {
      setLocalFilters((prev) => ({ ...prev, selectedMode: filterOptions.modes[0] }));
    } else if (localFilters.selectedTools.length === 0 && localFilters.selectedMode) {
      setLocalFilters((prev) => ({ ...prev, selectedMode: null }));
    }
  }, [localFilters.selectedTools.length]);

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <Typography variant="h6" component="span">
          Advanced Filters
        </Typography>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            {showPackages && (
              <PackagesFilterOption
                options={filterOptions.packages}
                value={localFilters.selectedPackages}
                onChange={(v) => setLocalFilters((prev) => ({ ...prev, selectedPackages: v }))}
              />
            )}

            {showTools && (
              <ToolsFilterOption
                options={filterOptions.tools}
                value={localFilters.selectedTools}
                onChange={(v) => setLocalFilters((prev) => ({ ...prev, selectedTools: v }))}
                modeOptions={filterOptions.modes}
                selectedMode={localFilters.selectedMode}
                onSelectedModeChange={(mode) =>
                  setLocalFilters((prev) => ({ ...prev, selectedMode: mode }))
                }
              />
            )}

            {showRoles && (
              <RolesFilterOption
                options={filterOptions.projectUserRoles}
                value={localFilters.selectedRoles}
                onChange={(v) => setLocalFilters((prev) => ({ ...prev, selectedRoles: v }))}
              />
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={() => setLocalFilters(defaultAdvancedFiltersState)} color="secondary">
          Reset
        </Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleApply} variant="contained">
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdvancedFiltersDialog;
